/* eslint-disable no-irregular-whitespace */
import React from 'react';

import { ModalTitle, ModalTypography } from 'components/Modal/Modal';
import { ModalStep } from 'components/Flow/components/ModalStep';
import DeedRaisedBoxIcon from 'assets/deed-raised-box.avif';
import { StepComponentProps } from 'components/Flow/RenderMachine';
import Img from 'retired/Img';

import { Options } from './Options';
import { Privileges } from 'utils/privileges';
import { usePartners } from 'entities/organization/usePartners';
import { useSelector } from 'react-redux';
import { selectUser } from 'entities/user/selectors';
import { NonprofitEntityType } from 'entities/organization/model';
import { Button, CircularProgress } from '@mui/material';
import { Link } from 'react-router-dom';

const ModalBody = ({
  send,
  isWalmartPartner,
  nonprofitEntityType,
  verifiedUsers,
}: {
  send: StepComponentProps['send'];
  isWalmartPartner: boolean;
  nonprofitEntityType?: NonprofitEntityType;
  verifiedUsers: Array<{ email: string }>;
}) => {
  if (isWalmartPartner) {
    return (
      <>
        <ModalTitle>You're All Set — Deed Onboarding Complete! 🎉</ModalTitle>
        <ModalTypography>
          Great job! You’ve successfully completed onboarding with Deed, Walmart’s verification
          partner for Spark Good programs.
        </ModalTypography>
        <ModalTypography>
          📸 Tip: Take a screenshot or print this page for your records.
        </ModalTypography>
        {verifiedUsers.length > 0 && (
          <ModalTypography>
            📧 Account Owner Email{verifiedUsers.length > 0 ? 's' : ''}:{' '}
            {verifiedUsers.map((user) => user.email).join(', ')}
          </ModalTypography>
        )}
        <ModalTypography>
          Make sure you have a Walmart.com account using this same email to access Spark Good
          programs.
        </ModalTypography>
        <ModalTitle>⏳ What’s Next?</ModalTitle>
        <ModalTypography>
          It may take up to 24 hours for your information to sync with Walmart systems. While you
          wait, here are a few things you can do:
        </ModalTypography>

        <ModalTitle>👥 Add Verified Users</ModalTitle>
        <ul>
          <li>
            <ModalTypography style={{ marginBottom: 8 }}>
              Go to <Link to="/organizers">Settings {'>'} Users</Link>
            </ModalTypography>
          </li>
          <li>
            <ModalTypography style={{ marginBottom: 8 }}>
              Assign roles (see last page for role descriptions)
            </ModalTypography>
          </li>
          <li>
            <ModalTypography>
              Each user must have their own Walmart.com account to access additional programs.
            </ModalTypography>
          </li>
        </ul>

        <ModalTitle>📝 Complete Your Organization Profile</ModalTitle>
        <ul>
          <li>
            <ModalTypography>
              Go to <Link to="/organization">Settings {'>'} Nonprofit Profile</Link> to fill in your
              details
            </ModalTypography>
          </li>
        </ul>

        {(nonprofitEntityType === NonprofitEntityType.nonprofit ||
          nonprofitEntityType === NonprofitEntityType.school) && (
          <>
            <ModalTitle>📅 Post Volunteer Events</ModalTitle>
            <ul>
              <li>
                <ModalTypography>
                  Post opportunities for Walmart associates to get involved
                </ModalTypography>
              </li>
            </ul>

            <ModalTitle>💸 Donations & Disbursements</ModalTitle>
            <ModalTypography>
              Learn how your organization may receive donations and grant disbursements across all
              Spark Good programs,{' '}
              <a
                target="_blank"
                href={
                  {
                    nonprofit:
                      'https://www.walmart.org/content/dam/walmart-org/documents/what-we-do/strengthening-community/spark-good/spark-good-getting-started/public-charity-one-pager.pdf',
                    school:
                      'https://www.walmart.org/content/dam/walmart-org/documents/what-we-do/strengthening-community/spark-good/spark-good-getting-started/nces-one-pager.pdf',
                  }[nonprofitEntityType]
                }
              >
                here
              </a>
              .
            </ModalTypography>
          </>
        )}

        <ModalTypography>
          For additional information about Spark Good, please visit{' '}
          <a
            target="_blank"
            href="https://www.walmart.org/content/walmart-org/en_us/what-we-do/strengthening-community/spark-good.html"
          >
            here
          </a>
          .
        </ModalTypography>
      </>
    );
  }

  return (
    <>
      <ModalTitle>Congratulations! Onboarding Successfully Completed!</ModalTitle>
      <ModalTypography>
        Choose one for now. You can learn more about all of these on the ‘Manage Deeds’ tab, which
        will be on the left sidebar on the platform. Creating ‘deeds’ — our catch-all term for
        volunteer events, projects, and fundraisers — is the best way to get our companies’
        attention. Can’t wait to see you on the platform!
      </ModalTypography>

      <Options send={send} />
    </>
  );
};

export const NeedHelpStep = ({ send, state }: StepComponentProps): JSX.Element => {
  const verifiedUsers = state?.context?.nonprofit?.verifiedUsers;
  const user = useSelector(selectUser);
  const { data: walmartPartnerships, loading } = usePartners(
    { status: ['pending', 'accepted'], search: 'Walmart' },
    !user?.hasPrivilege(Privileges.PARTNER_VIEW)
  );

  const isWalmartPartner = walmartPartnerships.length > 0;

  return (
    <ModalStep
      showProgressBar
      header={<Img src={DeedRaisedBoxIcon} height={209} />}
      headerStyles={{
        paddingTop: 16,
      }}
      actions={
        isWalmartPartner ? (
          <Button
            type="button"
            variant="contained"
            onClick={() => {
              send({ type: 'DISMISS' });
            }}
          >
            Get Started
          </Button>
        ) : undefined
      }
    >
      {loading ? (
        <CircularProgress />
      ) : (
        <ModalBody
          send={send}
          verifiedUsers={verifiedUsers}
          isWalmartPartner={isWalmartPartner}
          nonprofitEntityType={user?.organization?.nonprofitEntityType}
        />
      )}
    </ModalStep>
  );
};
